import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../../components/AuthProvider';
import { CardDetailModal } from '../../components/CardDetailModal';
import { CardImage } from '../../components/CardImage';
import { GameSelector } from '../../components/GameSelector';
import { getGameSetsByGame, getUserCardsForCollection } from '../../services/supabaseService';
import { aggregateSetsWithUserCards, SetWithStats, GameSet, UserCardSummary } from '../../utils/setMatcher';
import { QuickFilters } from '../../components/QuickFilters';

interface CollectionsProps {
  activeGame: number | 'All';
  setActiveGame: (game: number | 'All') => void;
  dbGames: any[];
}

type FilterStatus = 'all' | 'owned' | 'complete' | 'missing';
type SortOption = 'recent' | 'oldest' | 'name-asc' | 'name-desc' | 'progress' | 'total-cards';

export const resolveSetType = (s: GameSet): string => {
  if (s.temp_type && s.temp_type.trim()) {
    const raw = s.temp_type.trim().toUpperCase();
    if (raw.includes('STARTER') || raw.includes('STRUCTURE') || raw.includes('DECK')) return 'Deck';
    if (raw.includes('BOOSTER') || raw.includes('EXPANSION')) return 'Booster / Expansão';
    if (raw.includes('PROMO')) return 'Promo';
    return s.temp_type.trim();
  }
  const name = (s.name || '').toUpperCase();
  if (name.includes('DECK') || name.includes('STARTER') || name.includes('STRUCTURE')) return 'Deck';
  if (name.includes('BOOSTER') || name.includes('EXPANSION') || name.includes('EXPANSÃO')) return 'Booster / Expansão';
  if (name.includes('PROMO') || name.includes('PROMOTIONAL')) return 'Promo';
  if (name.includes('COLLECTION') || name.includes('COLEÇÃO') || name.includes('SPECIAL') || name.includes('ESPECIAL')) return 'Coleção Especial';
  return 'Geral';
};

export const CollectionsPage: React.FC<CollectionsProps> = ({
  activeGame,
  setActiveGame,
  dbGames,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { gameId: paramGameId } = useParams<{ gameId?: string }>();
  const { user } = useAuth();
  const setsContainerRef = useRef<HTMLDivElement>(null);

  // Selected game - prioritize paramGameId in URL (by id or slug), otherwise activeGame
  const currentGameObj = useMemo(() => {
    if (paramGameId) {
      const found = dbGames.find((g: any) => 
        String(g.id) === String(paramGameId) || 
        String(g.slug || '').toLowerCase() === String(paramGameId).toLowerCase()
      );
      if (found) return found;
    }
    if (activeGame !== 'All' && activeGame) {
      const found = dbGames.find((g: any) => 
        Number(g.id) === Number(activeGame) || 
        String(g.slug || '').toLowerCase() === String(activeGame).toLowerCase()
      );
      if (found) return found;
    }
    return null;
  }, [paramGameId, activeGame, dbGames]);

  const currentGameId = useMemo(() => {
    return currentGameObj?.id ? Number(currentGameObj.id) : 1;
  }, [currentGameObj]);

  // Sync paramGameId with activeGame state (only when paramGameId is present)
  useEffect(() => {
    if (paramGameId && currentGameObj?.id && activeGame !== Number(currentGameObj.id)) {
      setActiveGame(Number(currentGameObj.id));
    }
  }, [paramGameId, currentGameObj, activeGame, setActiveGame]);

  const [sets, setSets] = useState<GameSet[]>([]);
  const [userCards, setUserCards] = useState<UserCardSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [sortOption, setSortOption] = useState<SortOption>('recent');

  // Pagination for smooth performance on low-end devices
  const [currentPage, setCurrentPage] = useState(1);
  const [setsPerPage, setSetsPerPage] = useState<number>(24);

  // Modal / Drawer state for inspecting owned cards in a specific set
  const [selectedSetForDetails, setSelectedSetForDetails] = useState<SetWithStats | null>(null);

  const [activeSetQuickFilters, setActiveSetQuickFilters] = useState<Record<string, string>>({});

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [currentGameId, searchQuery, statusFilter, sortOption, activeSetQuickFilters]);

  // Reset quick filters when game changes
  useEffect(() => {
    setActiveSetQuickFilters({});
  }, [currentGameId]);
  const [selectedCardForModal, setSelectedCardForModal] = useState<any | null>(null);

  // Load sets and user cards
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const loadData = async () => {
      try {
        const setsData = await getGameSetsByGame(currentGameId);
        let userCardsData: UserCardSummary[] = [];

        if (user) {
          userCardsData = await getUserCardsForCollection(user.id, currentGameId);
        }

        if (isMounted) {
          setSets(setsData || []);
          setUserCards(userCardsData || []);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error loading collections data:', err);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [currentGameId, user]);

  // Aggregate stats per set
  const setsWithStats = useMemo(() => {
    return aggregateSetsWithUserCards(sets, userCards);
  }, [sets, userCards]);

  // Global game stats
  const globalStats = useMemo(() => {
    const totalSets = sets.length;
    const totalCardsInGame = sets.reduce((sum, s) => sum + (s.cards_included || 0), 0);
    const uniqueOwnedGlobal = new Set(userCards.map(c => String(c.card_id || c.external_id || c.name))).size;
    const totalCopiesGlobal = userCards.reduce((sum, c) => sum + (typeof c.quantity === 'number' ? c.quantity : 1), 0);
    const globalRate = totalCardsInGame > 0 ? Math.min(100, Math.round((uniqueOwnedGlobal / totalCardsInGame) * 1000) / 10) : 0;

    return {
      totalSets,
      totalCardsInGame,
      uniqueOwnedGlobal,
      totalCopiesGlobal,
      globalRate,
    };
  }, [sets, userCards]);

  const setsQuickOptions = useMemo(() => {
    const types = new Set<string>();
    const years = new Set<string>();

    sets.forEach(s => {
      const resolvedType = resolveSetType(s);
      if (resolvedType) {
        types.add(resolvedType);
      }
      if (s.release_date && s.release_date.length >= 4) {
        const y = s.release_date.slice(0, 4);
        if (/^\d{4}$/.test(y)) {
          years.add(y);
        }
      }
    });

    const opts: Record<string, string[]> = {};
    if (types.size > 1) {
      opts['tipo'] = Array.from(types).sort();
    }
    if (years.size > 1) {
      opts['ano'] = Array.from(years).sort().reverse().slice(0, 8);
    }

    return Object.keys(opts).length > 0 ? opts : null;
  }, [sets]);

  // Filter and sort sets
  const filteredSets = useMemo(() => {
    let list = [...setsWithStats];

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(s => {
        const name = (s.name || '').toLowerCase();
        const code = (s.code || '').toLowerCase();
        const ext = (s.external_id || '').toLowerCase();
        return name.includes(q) || code.includes(q) || ext.includes(q);
      });
    }

    // Status filter
    if (statusFilter === 'owned') {
      list = list.filter(s => s.uniqueOwnedCount > 0);
    } else if (statusFilter === 'complete') {
      list = list.filter(s => s.isComplete);
    } else if (statusFilter === 'missing') {
      list = list.filter(s => s.completionRate < 100);
    }

    // Quick Filters (tipo, ano)
    if (activeSetQuickFilters.tipo) {
      list = list.filter(s => resolveSetType(s).toLowerCase() === activeSetQuickFilters.tipo.toLowerCase());
    }
    if (activeSetQuickFilters.ano) {
      list = list.filter(s => (s.release_date || '').startsWith(activeSetQuickFilters.ano));
    }

    // Sorting
    list.sort((a, b) => {
      if (sortOption === 'recent') {
        const dateA = a.release_date ? new Date(a.release_date).getTime() : 0;
        const dateB = b.release_date ? new Date(b.release_date).getTime() : 0;
        return dateB - dateA;
      }
      if (sortOption === 'oldest') {
        const dateA = a.release_date ? new Date(a.release_date).getTime() : 9999999999999;
        const dateB = b.release_date ? new Date(b.release_date).getTime() : 9999999999999;
        return dateA - dateB;
      }
      if (sortOption === 'name-asc') {
        return (a.name || '').localeCompare(b.name || '', 'pt-BR');
      }
      if (sortOption === 'name-desc') {
        return (b.name || '').localeCompare(a.name || '', 'pt-BR');
      }
      if (sortOption === 'progress') {
        return b.completionRate - a.completionRate || b.uniqueOwnedCount - a.uniqueOwnedCount;
      }
      if (sortOption === 'total-cards') {
        return (b.cards_included || 0) - (a.cards_included || 0);
      }
      return 0;
    });

    return list;
  }, [setsWithStats, searchQuery, statusFilter, sortOption, activeSetQuickFilters]);

  const totalPages = Math.max(1, Math.ceil(filteredSets.length / setsPerPage));
  const paginatedSets = useMemo(() => {
    if (setsPerPage >= filteredSets.length) return filteredSets;
    const start = (currentPage - 1) * setsPerPage;
    return filteredSets.slice(start, start + setsPerPage);
  }, [filteredSets, currentPage, setsPerPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    if (setsContainerRef.current) {
      setsContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const formatReleaseDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      const parts = dateStr.split('-');
      if (parts.length >= 2) {
        const year = parts[0];
        const month = parts[1];
        const monthNames: Record<string, string> = {
          '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
          '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
          '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez'
        };
        return `${monthNames[month] || month} ${year}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  // Se nenhum jogo estiver selecionado (activeGame === 'All' ou !currentGameObj), exibe o GameSelector no padrão exato das outras páginas
  if (activeGame === 'All' || !activeGame || !currentGameObj) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center">
        <GameSelector
          games={dbGames}
          selectedGameId={activeGame}
          onSelectGame={(id) => {
            const num = Number(id);
            setActiveGame(num);
            navigate(`/colecoes/${num}`);
          }}
          title="Escolha um Jogo"
          subtitle="Para explorar as coleções oficiais e acompanhar seu progresso de cartas, selecione uma franquia de TCG:"
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-16">
      {/* Header & Title Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800/80 pb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight flex items-center gap-3">
            Coleções de {currentGameObj?.name || 'TCG'}
          </h1>
        </div>

        {/* Game Selector Dropdown / Badges */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setActiveGame('All');
              navigate('/colecoes');
            }}
            className="text-xs text-purple-400 hover:text-purple-300 font-bold flex items-center gap-1.5 px-3 py-2 rounded-xl bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 transition-all cursor-pointer shrink-0"
            title="Escolher outro jogo"
          >
            <i className="fa-solid fa-arrows-rotate text-[11px]"></i>
            <span>Trocar Jogo</span>
          </button>
          <select
            value={currentGameId}
            onChange={(e) => {
              const newId = Number(e.target.value);
              setActiveGame(newId);
              navigate(`/colecoes/${newId}`);
            }}
            className="bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-500 font-medium hover:border-slate-600 transition-colors cursor-pointer"
          >
            {dbGames
              .filter(g => g.id && !isNaN(Number(g.id)))
              .map(g => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Global Progress & Statistics Banner */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Coleções</span>
            <i className="fa-solid fa-boxes-stacked text-purple-400 text-sm"></i>
          </div>
          <div>
            <div className="text-2xl font-bold text-white tracking-tight">
              {isLoading ? '...' : globalStats.totalSets}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              expansões registradas
            </div>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Cartas no Jogo</span>
            <i className="fa-solid fa-clone text-blue-400 text-sm"></i>
          </div>
          <div>
            <div className="text-2xl font-bold text-white tracking-tight">
              {isLoading ? '...' : globalStats.totalCardsInGame.toLocaleString('pt-BR')}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              cartas oficiais no catálogo
            </div>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Suas Cartas</span>
            <i className="fa-solid fa-folder-closed text-emerald-400 text-sm"></i>
          </div>
          <div>
            <div className="text-2xl font-bold text-white tracking-tight">
              {isLoading ? (
                '...'
              ) : user ? (
                <span>{globalStats.uniqueOwnedGlobal} <span className="text-xs font-normal text-slate-400">únicas</span></span>
              ) : (
                <span className="text-sm font-medium text-slate-400">Entre na conta</span>
              )}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              {user ? (
                <span>{globalStats.totalCopiesGlobal} cópias no total</span>
              ) : (
                'para rastrear progresso'
              )}
            </div>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Conclusão Global</span>
            <i className="fa-solid fa-chart-pie text-amber-400 text-sm"></i>
          </div>
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-white tracking-tight">
                {isLoading ? '...' : user ? `${globalStats.globalRate}%` : '0%'}
              </span>
              {user && globalStats.uniqueOwnedGlobal > 0 && (
                <span className="text-xs text-emerald-400 font-medium">
                  {globalStats.uniqueOwnedGlobal} / {globalStats.totalCardsInGame}
                </span>
              )}
            </div>
            <div className="w-full bg-slate-800 rounded-full h-1.5 mt-2 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-purple-500 to-emerald-400"
                style={{ width: `${Math.max(0, Math.min(100, globalStats.globalRate))}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Guest Banner if not logged in */}
      {!user && (
        <div className="bg-gradient-to-r from-purple-950/40 via-slate-900 to-slate-900 border border-purple-500/20 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400 shrink-0 mt-0.5">
              <i className="fa-solid fa-sparkles text-lg"></i>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">
                Monitore sua coleção completa
              </h4>
              <p className="text-xs text-slate-400 mt-0.5">
                Faça login para contabilizar automaticamente suas cartas em cada expansão e saber quais faltam para fechar seus sets.
              </p>
            </div>
          </div>
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all shadow-lg shadow-purple-600/20 shrink-0"
          >
            Entrar ou Criar Conta
          </Link>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4 space-y-4">
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
          {/* Search Field */}
          <div className="relative flex-1">
            <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar coleção por nome, código (ex: OP05, TEU, BT14)..."
              className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 rounded-xl pl-9 pr-8 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
              >
                <i className="fa-solid fa-xmark text-xs"></i>
              </button>
            )}
          </div>

          {/* Sort Selector */}
          <div className="flex items-center gap-2 shrink-0">
            <label className="text-xs text-slate-400 font-medium whitespace-nowrap">
              Ordenar:
            </label>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as SortOption)}
              className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-purple-500 font-medium cursor-pointer"
            >
              <option value="recent">Lançamento: Mais Recentes</option>
              <option value="oldest">Lançamento: Mais Antigos</option>
              <option value="progress">Maior Progresso (%)</option>
              <option value="total-cards">Maior Número de Cartas</option>
              <option value="name-asc">Nome: A - Z</option>
              <option value="name-desc">Nome: Z - A</option>
            </select>
          </div>
        </div>

        {/* Quick Filter Tabs */}
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-800/60">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              statusFilter === 'all'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'bg-slate-800/80 hover:bg-slate-800 text-slate-300'
            }`}
          >
            Todas as Coleções ({sets.length})
          </button>

          {user && (
            <>
              <button
                onClick={() => setStatusFilter('owned')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  statusFilter === 'owned'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'bg-slate-800/80 hover:bg-slate-800 text-slate-300'
                }`}
              >
                Com Cartas Minhas ({setsWithStats.filter(s => s.uniqueOwnedCount > 0).length})
              </button>
              <button
                onClick={() => setStatusFilter('complete')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  statusFilter === 'complete'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-slate-800/80 hover:bg-slate-800 text-slate-300'
                }`}
              >
                Completas 100% ({setsWithStats.filter(s => s.isComplete).length})
              </button>
              <button
                onClick={() => setStatusFilter('missing')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  statusFilter === 'missing'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'bg-slate-800/80 hover:bg-slate-800 text-slate-300'
                }`}
              >
                Incompletas ({setsWithStats.filter(s => s.completionRate < 100).length})
              </button>
            </>
          )}
        </div>
      </div>

      {/* Quick Filters Reused Component */}
      {setsQuickOptions && (
        <QuickFilters
          customQuickOptions={setsQuickOptions}
          activeFilters={activeSetQuickFilters}
          setActiveFilters={setActiveSetQuickFilters}
        />
      )}

      {/* Collections Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(n => (
            <div
              key={n}
              className="bg-slate-900/40 border border-slate-800/80 rounded-2xl p-5 animate-pulse space-y-4"
            >
              <div className="flex items-center justify-between">
                <div className="w-16 h-6 bg-slate-800 rounded-lg"></div>
                <div className="w-24 h-4 bg-slate-800 rounded"></div>
              </div>
              <div className="w-3/4 h-5 bg-slate-800 rounded"></div>
              <div className="w-full h-12 bg-slate-800/60 rounded-xl"></div>
              <div className="flex gap-2">
                <div className="flex-1 h-9 bg-slate-800 rounded-xl"></div>
                <div className="w-24 h-9 bg-slate-800 rounded-xl"></div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredSets.length === 0 ? (
        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-800/80 flex items-center justify-center text-slate-500 mx-auto mb-3">
            <i className="fa-solid fa-magnifying-glass text-xl"></i>
          </div>
          <h3 className="text-base font-bold text-white mb-1">
            Nenhuma coleção encontrada
          </h3>
          <p className="text-xs text-slate-400 max-w-sm mx-auto mb-4">
            {searchQuery
              ? `Não foram encontradas coleções com o termo "${searchQuery}". Tente limpar os filtros.`
              : 'Não há coleções registradas para este jogo no momento.'}
          </p>
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
              }}
              className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all"
            >
              Limpar Filtros
            </button>
          )}
        </div>
      ) : (
        <div ref={setsContainerRef} className="scroll-mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedSets.map(set => {
              const formattedDate = formatReleaseDate(set.release_date);
              const isFinished = set.isComplete;
              const hasCards = set.uniqueOwnedCount > 0;
              const inMaintenance = Boolean(set.maintenance);

              return (
                <div
                  key={set.id}
                  className={`bg-slate-900/80 border transition-all duration-200 rounded-2xl p-5 flex flex-col justify-between group hover:shadow-xl hover:shadow-purple-950/20 ${
                  inMaintenance
                    ? 'border-amber-500/35 bg-slate-900/90 shadow-lg shadow-amber-500/5'
                    : isFinished
                    ? 'border-emerald-500/40 bg-emerald-950/10'
                    : hasCards
                    ? 'border-purple-500/30'
                    : 'border-slate-800 hover:border-slate-700'
                }`}
              >
                <div className="space-y-3">
                  {/* Card Header: Code Badge & Release Date */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold px-2.5 py-1 rounded-lg bg-purple-950/80 text-purple-300 border border-purple-500/30 tracking-wide">
                        {set.code || 'EXP'}
                      </span>
                      {inMaintenance && (
                      <span className="text-[10px] uppercase tracking-wider font-extrabold text-amber-400 bg-amber-500/15 px-2 py-0.5 rounded border border-amber-500/30">
                        em Breve
                      </span>
                      )}
                    </div>

                    {formattedDate && (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <i className="fa-regular fa-calendar text-[10px]"></i>
                        {formattedDate}
                      </span>
                    )}
                  </div>

                  {/* Set Name */}
                  <div>
                    {inMaintenance ? (
                      <h3 className="text-base font-bold text-slate-400 line-clamp-2 leading-snug cursor-not-allowed">
                        {set.name}
                      </h3>
                    ) : (
                      <Link
                        to={`/colecoes/${currentGameId}/set/${set.id}`}
                        className="text-base font-bold text-white hover:text-purple-300 group-hover:text-purple-300 transition-colors line-clamp-2 leading-snug flex items-center justify-between gap-1.5 cursor-pointer"
                        title={`Explorar cartas de ${set.name}`}
                      >
                        <span className="hover:underline">{set.name}</span>
                        <i className="fa-solid fa-arrow-right text-[11px] opacity-0 group-hover:opacity-100 transition-opacity text-purple-400 shrink-0"></i>
                      </Link>
                    )}
                    {set.temp_type && (
                      <span className="text-[11px] text-slate-400 font-medium">
                        {set.temp_type}
                      </span>
                    )}
                    
                  </div>

                  {/* Stats & Progress Box */}
                  <div className="bg-slate-950/80 border border-slate-800/80 rounded-xl p-3.5 space-y-2.5">
                    {/* Numbers row */}
                    <div className="flex items-center justify-between text-xs">
                      <div>
                        <span className="text-slate-400 text-[11px]">Total da Coleção:</span>
                        <div className="font-bold text-slate-200">
                          {set.cards_included ? `${set.cards_included} cartas` : 'Não especificado'}
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="text-slate-400 text-[11px]">Você Possui:</span>
                        <div className="font-bold text-white flex items-center justify-end gap-1.5">
                          <span>{user ? set.uniqueOwnedCount : 0}</span>
                          <span className="text-slate-500">/</span>
                          <span className="text-slate-400">{set.cards_included || '?'}</span>
                          {user && (
                            <span
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded ml-1 ${
                                isFinished
                                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                  : hasCards
                                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                  : 'bg-slate-800 text-slate-400'
                              }`}
                            >
                              {set.completionRate}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-900 rounded-full h-2 overflow-hidden border border-slate-800/60">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          isFinished
                            ? 'bg-emerald-400'
                            : hasCards
                            ? 'bg-gradient-to-r from-purple-500 to-indigo-400'
                            : 'bg-slate-800'
                        }`}
                        style={{ width: `${Math.max(0, Math.min(100, user ? set.completionRate : 0))}%` }}
                      />
                    </div>

                    {/* Extra detail if user owns duplicate copies */}
                    {user && set.totalCopiesCount > set.uniqueOwnedCount && (
                      <div className="text-[11px] text-slate-400 flex items-center justify-between pt-0.5">
                        <span>Cópias repetidas:</span>
                        <span className="text-slate-300 font-medium">
                          {set.totalCopiesCount} cópias no total
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
              </div>
            );
          })}
          </div>

          {/* Pagination Controls */}
          {filteredSets.length > 0 && (
            <div className="mt-6 pt-5 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-slate-400 font-medium">
                Mostrando <span className="font-bold text-white">{(currentPage - 1) * setsPerPage + 1}</span> a <span className="font-bold text-white">{Math.min(filteredSets.length, currentPage * setsPerPage)}</span> de <span className="font-bold text-white">{filteredSets.length}</span> coleções
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-purple-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                    aria-label="Página anterior"
                  >
                    <i className="fa-solid fa-chevron-left text-[10px]"></i>
                    <span className="hidden sm:inline">Anterior</span>
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }).map((_, idx) => {
                      const pageNum = idx + 1;
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            type="button"
                            onClick={() => handlePageChange(pageNum)}
                            className={`min-w-8 h-8 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                              currentPage === pageNum
                                ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                                : 'bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:border-slate-700'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return (
                          <span key={pageNum} className="text-slate-600 text-xs px-1">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-purple-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                    aria-label="Próxima página"
                  >
                    <span className="hidden sm:inline">Próxima</span>
                    <i className="fa-solid fa-chevron-right text-[10px]"></i>
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Por página:</span>
                <select
                  value={setsPerPage}
                  onChange={e => {
                    setSetsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="bg-slate-900 border border-slate-800 rounded-lg px-2.5 py-1 text-slate-200 text-xs font-semibold focus:outline-none focus:border-purple-500 cursor-pointer"
                >
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                  <option value={9999}>Todas</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Drawer / Modal: Minhas Cartas Nesta Coleção */}
      <AnimatePresence>
        {selectedSetForDetails && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedSetForDetails(null)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative w-full max-w-3xl max-h-[88vh] bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl flex flex-col z-10"
            >
              {/* Modal Header */}
              <div className="p-5 sm:p-6 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs font-bold px-2.5 py-1 rounded-lg bg-purple-950 text-purple-400 border border-purple-500/30">
                    {selectedSetForDetails.code || 'SET'}
                  </span>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-white line-clamp-1">
                      {selectedSetForDetails.name}
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Você possui {selectedSetForDetails.uniqueOwnedCount} de {selectedSetForDetails.cards_included || '?'} cartas ({selectedSetForDetails.completionRate}% do set completo)
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedSetForDetails(null)}
                  className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer shrink-0"
                >
                  <i className="fa-solid fa-xmark text-sm"></i>
                </button>
              </div>

              {/* Modal Body - Cards Grid */}
              <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4">
                {selectedSetForDetails.userCards.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 text-sm">
                    Nenhuma carta encontrada nesta coleção ainda.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                    {selectedSetForDetails.userCards.map((card) => {
                      return (
                        <div
                          key={card.user_card_id || card.external_id || card.id}
                          onClick={() => setSelectedCardForModal(card)}
                          className="bg-slate-950/80 border border-slate-800/80 hover:border-purple-500/50 rounded-2xl p-2.5 flex flex-col group cursor-pointer transition-all duration-150 hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-950/30"
                        >
                          {/* Card Thumbnail */}
                          <div className="relative aspect-[7/10] w-full rounded-xl overflow-hidden bg-slate-900 mb-2">
                            <CardImage
                              src={card.imageUrl || card.image_url}
                              alt={card.name}
                              gameId={currentGameId}
                              objectFit="cover"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            />
                            {/* Quantity badge */}
                            <div className="absolute top-2 right-2 bg-slate-950/90 border border-slate-700 text-white font-mono text-[11px] font-bold px-2 py-0.5 rounded-md shadow">
                              x{card.quantity}
                            </div>
                          </div>

                          {/* Card details */}
                          <div className="flex-1 flex flex-col justify-between">
                            <div>
                              <div className="text-xs font-bold text-slate-200 line-clamp-1 group-hover:text-purple-300 transition-colors">
                                {card.name}
                              </div>
                              <div className="flex items-center space-x-1.5 flex-wrap text-[11px] font-mono mt-0.5">
                                {card.code && (
                                  <span className="text-blue-300 font-bold">
                                    Cód: {card.code}
                                  </span>
                                )}
                                {card.external_id && (
                                  <span className="text-purple-300 text-[10px]">
                                    ID: {card.external_id}
                                  </span>
                                )}
                                {!card.code && !card.external_id && (
                                  <span className="text-slate-500">-</span>
                                )}
                              </div>
                            </div>
                            {card.raridade && (
                              <div className="text-[10px] text-slate-500 mt-1 font-medium">
                                {card.raridade}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-4 sm:p-5 border-t border-slate-800 bg-slate-950/60 flex items-center justify-between gap-3">
                <span className="text-xs text-slate-400">
                  Total de cópias físicas: <strong className="text-slate-200">{selectedSetForDetails.totalCopiesCount}</strong>
                </span>

                <Link
                  to={`/colecoes/${currentGameId}/set/${selectedSetForDetails.id}`}
                  onClick={() => setSelectedSetForDetails(null)}
                  className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-md shadow-purple-600/20"
                >
                  <span>Explorar Coleção Completa</span>
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </Link>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Card Detail Modal when clicking on any card in the drawer */}
      {selectedCardForModal && (
        <CardDetailModal
          card={selectedCardForModal}
          onClose={() => setSelectedCardForModal(null)}
          cardsList={selectedSetForDetails?.userCards || []}
          onSelectCard={(c) => setSelectedCardForModal(c)}
          gameName={currentGameObj?.name}
        />
      )}
    </div>
  );
};
